import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { DomainCardButton } from '../../domain-cards/ui';
import VitalAdjuster from '../../VitalAdjuster';
import CookingPotAnimation from '../../animations/CookingPotAnimation';
import { refreshTokens } from '../../../services/SimpleTokenService';
import { generateRecipeWithAI, formatCreditsMessage, isAIGenerationAvailable } from '../../../services/aiRecipeService';
import {
  getFlavorColorClass,
  getMatchingAlgorithm,
  BENEFIT_TYPES,
} from '../../../config/campaignFrameConfig';

/**
 * FlavorCookingPattern - Beast Feast cooking minigame pattern
 *
 * Handles the full cooking flow:
 * 1. Prepare: Select ingredients from inventory
 * 2. Rolling: Build dice pool, roll, match sets, smart removal
 * 3. Results: Distribute meal rating to benefits, save recipe
 *
 * Config properties used:
 * - inputResource: inventory key for ingredients
 * - matchingAlgorithm: algorithm ID for dice removal
 * - benefitDistribution: array of benefit type IDs
 * - recipeBonus: tier-based bonus tokens
 * - cookbookTracking: enable recipe saving
 * - aiGeneration: enable AI recipe generation
 * - triggersRest: refresh rest-based tokens on completion
 */
const FlavorCookingPattern = ({
  frameId,
  mechanicId,
  config,
  character,
  updateCharacter,
  allData,
  frameStaticData,
  onClose,
  campaignId,
  user,
}) => {
  // Cooking step state
  const [cookingStep, setCookingStep] = useState('prepare'); // prepare | rolling | results

  // Ingredient selection
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const availableIngredients = character.inventory?.[config.inputResource || 'ingredients'] || [];
  const cookbook = character.cookbook || [];

  // Dice pool state
  const [dicePool, setDicePool] = useState([]);
  const [matchedSets, setMatchedSets] = useState([]);
  const [mealRating, setMealRating] = useState(0);
  const [recipeTokens, setRecipeTokens] = useState(0);

  // Distribution state
  const [hpToHeal, setHpToHeal] = useState(0);
  const [stressToClear, setStressToClear] = useState(0);
  const [hopeToGain, setHopeToGain] = useState(0);
  const [scarsToClear, setScarsToClear] = useState(0);

  // Recipe state
  const [recipeName, setRecipeName] = useState('');
  const [recipeDescription, setRecipeDescription] = useState('');
  const [showCookbook, setShowCookbook] = useState(false);

  // AI generation state
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [aiCreditsRemaining, setAICreditsRemaining] = useState(5);

  // Armor marks state
  const [clearArmorMarks, setClearArmorMarks] = useState(false);

  // Animation state
  const [animationIngredients, setAnimationIngredients] = useState([]);
  const [animationResetTrigger, setAnimationResetTrigger] = useState(0);
  const [isStirring, setIsStirring] = useState(false);

  // Get flavor profiles from static data
  const flavorProfiles = frameStaticData?.customMechanics?.cooking?.flavorProfiles || [];

  // Get matching algorithm from config
  const matchingAlgorithm = getMatchingAlgorithm(config.matchingAlgorithm);

  /**
   * Check if a recipe can be recreated with available ingredients
   */
  const canRecreateRecipe = useCallback((recipe) => {
    if (!recipe.flavorTypes && !recipe.flavorProfile) return false;

    const requiredFlavors = recipe.flavorTypes || recipe.flavorProfile?.map(f => f.profile) || [];
    const availableFlavors = availableIngredients
      .flatMap(ing => ing.flavors?.map(f => f.profile) || []);

    return requiredFlavors.every(flavor => availableFlavors.includes(flavor));
  }, [availableIngredients]);

  /**
   * Check for recipe match and set bonus tokens
   */
  const checkForRecipeMatch = useCallback((selectedIngs) => {
    const currentProfile = selectedIngs
      .map(ing => ing.flavors.map(f => f.profile).sort().join('-'))
      .sort()
      .join('|');

    const matchingRecipe = cookbook.find(recipe =>
      recipe.flavorProfileKey === currentProfile || recipe.flavorProfile === currentProfile
    );

    if (matchingRecipe) {
      const tierBonus = config.recipeBonus?.getAmount?.(character) || character.tier || 1;
      setRecipeTokens(tierBonus);
      return matchingRecipe;
    }

    setRecipeTokens(0);
    return null;
  }, [cookbook, character, config.recipeBonus]);

  /**
   * Toggle ingredient selection
   */
  const toggleIngredientSelection = (ingredientId) => {
    setSelectedIngredients(prev => {
      const isSelected = prev.includes(ingredientId);
      const newSelected = isSelected
        ? prev.filter(id => id !== ingredientId)
        : [...prev, ingredientId];

      // Update animation ingredients
      if (!isSelected) {
        const ingredient = availableIngredients.find(ing => ing.id === ingredientId);
        if (ingredient) {
          setAnimationIngredients(current => [...current, ingredient]);
        }
      } else {
        setAnimationIngredients(current => current.filter(ing => ing.id !== ingredientId));
      }

      return newSelected;
    });
  };

  /**
   * Begin cooking - build dice pool from selected ingredients
   */
  const beginCooking = () => {
    setDicePool([]);
    setMatchedSets([]);
    setMealRating(0);
    setHpToHeal(0);
    setStressToClear(0);
    setHopeToGain(0);
    setScarsToClear(0);
    setIsStirring(true);

    const ingredientsToCook = availableIngredients.filter(ing =>
      selectedIngredients.includes(ing.id)
    );

    // Check for recipe match
    checkForRecipeMatch(ingredientsToCook);

    // Build dice pool from ingredient flavors
    let pool = [];
    ingredientsToCook.forEach(ingredient => {
      ingredient.flavors?.forEach(flavor => {
        const profile = flavorProfiles.find(p => p.name === flavor.profile);
        if (profile) {
          for (let i = 0; i < (flavor.strength || 1); i++) {
            pool.push({
              sides: parseInt(profile.die.slice(1)),
              value: null,
              id: `die-${pool.length}`,
              ingredientName: ingredient.name,
            });
          }
        }
      });
    });

    // Add recipe bonus tokens
    for (let i = 0; i < recipeTokens; i++) {
      pool.push({
        sides: 0,
        value: 'TOKEN',
        id: `token-${i}`,
        ingredientName: 'Recipe Bonus',
        isToken: true,
      });
    }

    setDicePool(pool);
    setCookingStep('rolling');
  };

  /**
   * Roll dice pool and handle matching/removal
   */
  const rollDicePool = () => {
    setIsStirring(true);

    // Roll all dice
    const rolledDice = dicePool
      .filter(die => !die.isToken)
      .map(die => ({
        ...die,
        value: Math.ceil(Math.random() * die.sides),
      }));

    // Count occurrences
    const counts = rolledDice.reduce((acc, die) => {
      acc[die.value] = (acc[die.value] || 0) + 1;
      return acc;
    }, {});

    // Find matches (2+ of same value)
    const matches = Object.entries(counts)
      .filter(([, count]) => count > 1)
      .map(([value]) => parseInt(value));

    if (matches.length > 0) {
      // Collect matched dice
      const matchedDice = rolledDice.filter(die => matches.includes(die.value));
      const remainingDice = rolledDice.filter(die => !matches.includes(die.value));

      setMatchedSets(prev => [...prev, ...matchedDice]);
      setDicePool(remainingDice.map(d => ({ ...d, value: null })));
    } else {
      // No matches - use algorithm to remove worst die
      const dieToRemove = matchingAlgorithm
        ? matchingAlgorithm(rolledDice)
        : rolledDice[0];

      const remainingDice = rolledDice.filter(die => die.id !== dieToRemove.id);
      setDicePool(remainingDice.map(d => ({ ...d, value: null })));
    }
  };

  /**
   * Effect: Check if cooking is complete
   */
  useEffect(() => {
    if (cookingStep === 'rolling' && dicePool.filter(d => !d.isToken).length <= 1) {
      // Calculate final meal rating
      const finalMatched = [...matchedSets, ...dicePool.filter(d => !d.isToken)];
      const finalCounts = finalMatched.reduce((acc, die) => {
        if (die.value && die.value !== 'TOKEN') {
          acc[die.value] = (acc[die.value] || 0) + 1;
        }
        return acc;
      }, {});

      const finalSets = Object.entries(finalCounts)
        .filter(([, count]) => count > 1)
        .map(([value]) => parseInt(value));

      const rating = finalSets.reduce((sum, val) => sum + val, 0);
      setMealRating(rating);
      setCookingStep('results');
    }
  }, [dicePool, matchedSets, cookingStep]);

  /**
   * Apply feast benefits to character
   */
  const applyFeastBenefits = () => {
    const totalSpent = hpToHeal + stressToClear + hopeToGain + scarsToClear;
    if (totalSpent > mealRating) {
      toast.error('Cannot spend more than the meal rating!');
      return;
    }

    // Calculate new values
    const newHp = Math.min(character.maxHp || 8, (character.hp || 0) + hpToHeal);
    const newStress = Math.max(0, (character.stress || 0) - stressToClear);
    const newHope = Math.min(6, (character.hope || 0) + hopeToGain);

    // Handle scar clearing
    let newScars = [...(character.scars || [])];
    if (scarsToClear > 0) {
      newScars.sort((a, b) => (b.hopeSlot || 0) - (a.hopeSlot || 0));
      newScars = newScars.slice(scarsToClear);
    }

    // Remove used ingredients
    const remainingIngredients = availableIngredients.filter(
      ing => !selectedIngredients.includes(ing.id)
    );

    // Build cookbook entry if name/description provided
    let newCookbook = [...cookbook];
    if (config.cookbookTracking && recipeName.trim() && recipeDescription.trim()) {
      const usedIngredients = availableIngredients.filter(ing =>
        selectedIngredients.includes(ing.id)
      );

      // Generate flavor profile key
      const flavorProfileKey = usedIngredients
        .map(ing => ing.flavors?.map(f => f.profile).sort().join('-') || '')
        .sort()
        .join('|');

      // Extract flavor objects
      const flavorMap = new Map();
      usedIngredients.forEach(ing => {
        ing.flavors?.forEach(f => {
          const profile = f.profile || f;
          const strength = f.strength || 1;
          flavorMap.set(profile, Math.max(flavorMap.get(profile) || 0, strength));
        });
      });

      const flavorObjects = Array.from(flavorMap.entries())
        .map(([profile, strength]) => ({ profile, strength }))
        .sort((a, b) => b.strength - a.strength || a.profile.localeCompare(b.profile));

      const recipeEntry = {
        id: `recipe-${Date.now()}`,
        name: recipeName.trim(),
        description: recipeDescription.trim(),
        ingredients: usedIngredients.map(ing => ({
          name: ing.name,
          source: ing.source,
          flavors: ing.flavors,
          ...(ing.isSpecial && { isSpecial: true, specialFeature: ing.specialFeature }),
        })),
        flavorProfile: flavorObjects,
        flavorProfileKey,
        mealRating,
        dateCreated: new Date().toLocaleDateString(),
        usedCount: 1,
      };

      // Only add if not duplicate
      if (!newCookbook.find(r => r.flavorProfileKey === flavorProfileKey)) {
        newCookbook.push(recipeEntry);
      }
    }

    // Build updated character
    let updatedCharacter = {
      ...character,
      hp: newHp,
      stress: newStress,
      hope: newHope,
      scars: newScars,
      cookbook: newCookbook,
      inventory: {
        ...character.inventory,
        [config.inputResource || 'ingredients']: remainingIngredients,
      },
      ...(clearArmorMarks && { armorMarks: [] }),
    };

    // Refresh rest-based abilities if configured (Beast Feast counts as a long rest)
    if (config.triggersRest) {
      const spellcastTrait = allData?.subclasses?.[character.subclass]?.spellcastTrait || null;
      updatedCharacter = refreshTokens(updatedCharacter, 'long-rest', spellcastTrait);
      toast.success('🔄 Rest abilities refreshed!', { duration: 3000 });
    }

    updateCharacter(updatedCharacter);
    setIsStirring(false);

    toast.success('🍽️ Feast complete!');
    if (onClose) onClose();
  };

  /**
   * Reset cooking state
   */
  const resetCooking = () => {
    setSelectedIngredients([]);
    setDicePool([]);
    setMatchedSets([]);
    setMealRating(0);
    setCookingStep('prepare');
    setAnimationIngredients([]);
    setAnimationResetTrigger(prev => prev + 1);
    setIsStirring(false);
    setHpToHeal(0);
    setStressToClear(0);
    setHopeToGain(0);
    setScarsToClear(0);
    setRecipeTokens(0);
    setRecipeName('');
    setRecipeDescription('');
    setClearArmorMarks(false);
  };

  /**
   * AI recipe generation
   */
  const handleAIGeneration = async () => {
    if (isGeneratingAI || !config.aiGeneration) return;

    setIsGeneratingAI(true);

    try {
      const usedIngredients = availableIngredients.filter(ing =>
        selectedIngredients.includes(ing.id)
      );

      const result = await generateRecipeWithAI({
        ingredients: usedIngredients,
        mealRating,
        campaignId: campaignId || `solo-${character.id}`,
        userId: user?.uid || 'unknown',
      });

      if (result.success) {
        setRecipeName(result.name);
        setRecipeDescription(result.description);
        setAICreditsRemaining(result.creditsRemaining);
        if (result.cached) {
          toast.success('Recipe retrieved from history!', { duration: 3000 });
        }
      } else {
        if (result.creditsRemaining !== null && result.creditsRemaining !== undefined) {
          setAICreditsRemaining(result.creditsRemaining);
        }
        toast.error(result.error || 'Recipe generation failed. Try entering details manually.', { duration: 5000 });
      }
    } catch (error) {
      console.error('AI recipe generation error:', error);
      toast.error('Recipe generation failed unexpectedly. Try entering details manually.', { duration: 5000 });
    } finally {
      setIsGeneratingAI(false);
    }
  };

  // No ingredients available
  if (availableIngredients.length === 0) {
    return (
      <div className="space-y-6 text-center">
        <h3 className="text-xl font-bold text-orange-600 dark:text-orange-400">
          🍽️ Make a Feast
        </h3>
        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-6 rounded-lg border border-yellow-200 dark:border-yellow-800">
          <p className="text-yellow-800 dark:text-yellow-200 mb-2">
            📦 No ingredients in inventory
          </p>
          <p className="text-sm text-yellow-700 dark:text-yellow-300">
            Harvest ingredients from defeated beasts first.
          </p>
        </div>
        <DomainCardButton onClick={onClose} variant="secondary">
          Close
        </DomainCardButton>
      </div>
    );
  }

  const totalSpent = hpToHeal + stressToClear + hopeToGain + scarsToClear;
  const remainingBudget = mealRating - totalSpent;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="flex items-center justify-center gap-3 mb-2">
          <h3 className="text-xl font-bold text-orange-600 dark:text-orange-400">
            🍽️ Make a Feast
          </h3>
          {config.cookbookTracking && (
            <DomainCardButton
              onClick={() => setShowCookbook(!showCookbook)}
              variant="primary"
              className="text-sm"
            >
              📖 {showCookbook ? 'Hide' : 'View'} Cookbook
            </DomainCardButton>
          )}
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Combine ingredients to cook a meal and recover during downtime
        </p>
      </div>

      {/* Cooking Animation */}
      <div className="flex justify-center">
        <CookingPotAnimation
          ingredients={animationIngredients}
          resetTrigger={animationResetTrigger}
          isExternallyStirring={isStirring}
          externalCookingPhase={cookingStep === 'rolling' ? 'cooking' : 'prepare'}
        />
      </div>

      {/* Cookbook View */}
      {showCookbook && (
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
          <h4 className="font-semibold mb-3 text-blue-800 dark:text-blue-200">
            📖 Party Cookbook
          </h4>
          {cookbook.length === 0 ? (
            <p className="text-blue-700 dark:text-blue-300">
              No recipes recorded yet. Cook your first feast!
            </p>
          ) : (
            <div className="space-y-3 max-h-48 overflow-y-auto">
              {cookbook.map((recipe, index) => (
                <div key={index} className="bg-white dark:bg-gray-700 p-3 rounded-lg border">
                  <div className="flex justify-between items-start mb-2">
                    <h5 className="font-medium">{recipe.name}</h5>
                    <div className="flex items-center gap-2">
                      {canRecreateRecipe(recipe) && (
                        <DomainCardButton
                          onClick={() => {
                            // Auto-select matching ingredients
                            const requiredFlavors = recipe.flavorProfile?.map(f => f.profile) || [];
                            const selectedIds = [];
                            requiredFlavors.forEach(flavor => {
                              const match = availableIngredients.find(ing =>
                                !selectedIds.includes(ing.id) &&
                                ing.flavors?.some(f => f.profile === flavor)
                              );
                              if (match) selectedIds.push(match.id);
                            });
                            setSelectedIngredients(selectedIds);
                            setRecipeName(recipe.name);
                            setShowCookbook(false);
                          }}
                          variant="activeSuccess"
                          className="text-xs"
                        >
                          🍳 Recreate
                        </DomainCardButton>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {recipe.description}
                  </p>
                  <p className="text-xs text-gray-500">
                    Rating: {recipe.mealRating} | Made: {recipe.dateCreated}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* PREPARE PHASE */}
      {cookingStep === 'prepare' && (
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
            <h4 className="font-semibold mb-3">
              Select Ingredients ({availableIngredients.length} available)
            </h4>
            <div className="max-h-48 overflow-y-auto space-y-2 border rounded-lg p-2 bg-white dark:bg-gray-700">
              {availableIngredients.map(ing => (
                <label
                  key={ing.id}
                  className="flex items-center p-3 bg-gray-50 dark:bg-gray-600/50 rounded-md cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                >
                  <input
                    type="checkbox"
                    checked={selectedIngredients.includes(ing.id)}
                    onChange={() => toggleIngredientSelection(ing.id)}
                    className="h-4 w-4 rounded text-orange-600 focus:ring-orange-500"
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{ing.name}</span>
                        {ing.isSpecial && (
                          <span className="text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 px-2 py-1 rounded">
                            ⭐ Special
                          </span>
                        )}
                      </div>
                      {ing.source && (
                        <span className="text-xs text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded">
                          {ing.source}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {ing.flavors?.map(f => `${f.profile} (${f.strength})`).join(', ')}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {selectedIngredients.length > 0 && (
            <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border border-orange-200 dark:border-orange-800">
              <p className="text-orange-800 dark:text-orange-200 text-sm font-medium">
                Selected: {selectedIngredients.length} ingredient(s)
              </p>
              {recipeTokens > 0 && (
                <p className="text-green-700 dark:text-green-300 text-xs mt-1">
                  📖 Recipe match! +{recipeTokens} bonus tokens
                </p>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <DomainCardButton
              onClick={beginCooking}
              disabled={selectedIngredients.length === 0}
              variant="primary"
              className="flex-1"
            >
              Start Cooking
            </DomainCardButton>
            <DomainCardButton onClick={onClose} variant="secondary">
              Cancel
            </DomainCardButton>
          </div>
        </div>
      )}

      {/* ROLLING PHASE */}
      {cookingStep === 'rolling' && (
        <div className="space-y-4">
          <div className="text-center">
            <h4 className="text-lg font-semibold mb-2">🎲 Cooking Process</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Roll dice and collect matching sets.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Dice Pool */}
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <h4 className="font-semibold mb-3">
                Dice Pool ({dicePool.filter(d => !d.isToken).length})
              </h4>
              <div className="flex flex-wrap justify-center gap-2 p-4 bg-white dark:bg-gray-700 rounded-lg min-h-[6rem]">
                {dicePool.map(die => (
                  <div
                    key={die.id}
                    className={`p-2 w-12 h-12 flex items-center justify-center font-bold text-lg rounded-md shadow ${
                      die.isToken
                        ? 'bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200 border-2 border-yellow-400'
                        : 'bg-gray-100 dark:bg-gray-600'
                    }`}
                    title={die.isToken ? 'Recipe bonus token' : `d${die.sides}`}
                  >
                    {die.isToken ? '🎫' : die.value || `d${die.sides}`}
                  </div>
                ))}
              </div>
            </div>

            {/* Matched Sets */}
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
              <h4 className="font-semibold mb-3 text-green-800 dark:text-green-200">
                Matched Sets ({matchedSets.length})
              </h4>
              <div className="flex flex-wrap justify-center gap-2 p-4 bg-white dark:bg-gray-700 rounded-lg min-h-[6rem]">
                {matchedSets.map(die => (
                  <div
                    key={die.id}
                    className="p-2 w-12 h-12 flex items-center justify-center font-bold text-lg bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-100 rounded-md shadow"
                  >
                    {die.value}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center">
            <DomainCardButton
              onClick={rollDicePool}
              disabled={dicePool.filter(d => !d.isToken).length <= 1}
              variant="primary"
            >
              Roll Dice
            </DomainCardButton>
          </div>
        </div>
      )}

      {/* RESULTS PHASE */}
      {cookingStep === 'results' && (
        <div className="space-y-6">
          {/* Meal Rating Display */}
          <div className="text-center bg-green-50 dark:bg-green-900/20 p-6 rounded-lg border border-green-200 dark:border-green-800">
            <h3 className="text-2xl font-bold text-green-600 dark:text-green-400 mb-2">
              🎉 Feast Complete!
            </h3>
            <p className="text-4xl font-bold text-green-700 dark:text-green-300">
              {mealRating}
            </p>
            <p className="text-sm text-green-600 dark:text-green-400">
              Meal Rating Points to Distribute
            </p>
          </div>

          {/* Distribution Controls */}
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
            <h4 className="font-semibold mb-4">Distribute Benefits</h4>

            {/* Budget Bar */}
            <div className="mb-6 p-4 bg-gray-900 rounded-lg border border-green-600">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-300">Available</span>
                <span className="text-2xl font-bold text-green-400">
                  {remainingBudget} / {mealRating}
                </span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-green-500 to-green-600 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${Math.max(0, (remainingBudget / mealRating) * 100)}%` }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <VitalAdjuster
                label="Heal HP"
                vitalType="hp"
                currentValue={character.hp || 0}
                maxValue={character.maxHp || 8}
                adjustmentValue={hpToHeal}
                onAdjustmentChange={(delta) => {
                  const maxAllowed = Math.min(
                    mealRating - (stressToClear + hopeToGain + scarsToClear),
                    (character.maxHp || 8) - (character.hp || 0)
                  );
                  setHpToHeal(Math.max(0, Math.min(maxAllowed, hpToHeal + delta)));
                }}
                remainingBudget={mealRating - (stressToClear + hopeToGain + scarsToClear)}
              />

              <VitalAdjuster
                label="Clear Stress"
                vitalType="stress"
                currentValue={character.stress || 0}
                maxValue={character.maxStress || 5}
                adjustmentValue={stressToClear}
                onAdjustmentChange={(delta) => {
                  const maxAllowed = Math.min(
                    mealRating - (hpToHeal + hopeToGain + scarsToClear),
                    character.stress || 0
                  );
                  setStressToClear(Math.max(0, Math.min(maxAllowed, stressToClear + delta)));
                }}
                remainingBudget={mealRating - (hpToHeal + hopeToGain + scarsToClear)}
              />

              <VitalAdjuster
                label="Gain Hope"
                vitalType="hope"
                currentValue={character.hope || 0}
                maxValue={6}
                adjustmentValue={hopeToGain}
                onAdjustmentChange={(delta) => {
                  const maxAllowed = Math.min(
                    mealRating - (hpToHeal + stressToClear + scarsToClear),
                    6 - (character.hope || 0)
                  );
                  setHopeToGain(Math.max(0, Math.min(maxAllowed, hopeToGain + delta)));
                }}
                remainingBudget={mealRating - (hpToHeal + stressToClear + scarsToClear)}
              />

              {/* Scars Input */}
              <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
                <label className="block text-sm font-medium mb-2 text-gray-300">
                  Clear Scars
                </label>
                <input
                  type="number"
                  value={scarsToClear}
                  onChange={e => {
                    const maxAllowed = Math.min(
                      mealRating - (hpToHeal + stressToClear + hopeToGain),
                      (character.scars || []).length
                    );
                    setScarsToClear(Math.max(0, Math.min(maxAllowed, parseInt(e.target.value) || 0)));
                  }}
                  min="0"
                  max={Math.min(remainingBudget, (character.scars || []).length)}
                  className="w-full p-3 rounded-md bg-gray-700 border border-gray-600 text-gray-100"
                />
                <p className="text-xs text-gray-400 mt-2">
                  Current: {(character.scars || []).length} scars
                </p>
              </div>

              {/* Clear Armor Marks */}
              {(character.armorMarks || []).length > 0 && (
                <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={clearArmorMarks}
                      onChange={e => setClearArmorMarks(e.target.checked)}
                      className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-300">
                        Repair Armor
                      </span>
                      <p className="text-xs text-gray-400">
                        Clear {(character.armorMarks || []).length} marked armor slot{(character.armorMarks || []).length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </label>
                </div>
              )}
            </div>

            {totalSpent > mealRating && (
              <p className="text-red-500 text-center mt-4">
                Cannot spend more than meal rating!
              </p>
            )}
          </div>

          {/* Cookbook Entry */}
          {config.cookbookTracking && (
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-semibold text-blue-800 dark:text-blue-200">
                  📖 Add to Cookbook
                </h4>
              </div>

              <div className="space-y-3">
                <div>
                  <div className="flex gap-2 items-end mb-1">
                    <label className="block text-sm font-medium flex-1">Recipe Name</label>
                    {config.aiGeneration && (
                      <DomainCardButton
                        onClick={handleAIGeneration}
                        disabled={!isAIGenerationAvailable(aiCreditsRemaining, isGeneratingAI)}
                        variant="primary"
                        className="text-xs"
                      >
                        {isGeneratingAI ? '⏳ Discovering...' : '✨ Discover Recipe'}
                      </DomainCardButton>
                    )}
                  </div>
                  <input
                    type="text"
                    value={recipeName}
                    onChange={e => setRecipeName(e.target.value)}
                    className="w-full p-2 rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600"
                    placeholder="e.g., Hearty Beast Stew"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    value={recipeDescription}
                    onChange={e => setRecipeDescription(e.target.value)}
                    className="w-full p-2 rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600"
                    placeholder="Describe the cooking method..."
                    rows="3"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <DomainCardButton
              onClick={applyFeastBenefits}
              disabled={totalSpent > mealRating}
              variant="activeSuccess"
              className="flex-1"
            >
              Finish Feast
            </DomainCardButton>
            <DomainCardButton onClick={resetCooking} variant="warning">
              Cook Again
            </DomainCardButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlavorCookingPattern;
